import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import { automaticApi } from "@/settings/api";
import environment from "@/settings/environment";
import { getFormattedPaidItems } from "@/utils/transform";

export interface PaidItem {
  cdConcessionaria: number;
  nmConcessionaria: string;
  placa: string;
  placaVeiculo: string;
  cdCatCobrada: number;
  cdPista: number;
  cdPraca: number;
  dataPassagem: string;
  dtPagamento: string;
  dtVencimento: string;
  dtVencimentoMulta: string | null;
  flMulta: number;
  fmtDataPassagem: string;
  fmtDtPagamento: string;
  idPassagemFreeFlow: string;
  nmPraca: string;
  qtDiasVencida: number;
  statusTransacao: string;
  valorPassagem: number;
  vlMulta: number;
  vlTotalPagar: number;
}

export interface CheckTicketStatusRequest {
  companyIdentification: string;
  channel: string;
  document: string;
  placa: string;
}

export interface CheckTicketStatusResponse {
  data?: PaidItem[];
  status?: {
    codigoRetorno: number;
    mensagem: string;
    sessionId: string;
  };
}

const fetchPaidItems = async (
  document: string,
  selectedVehicles: string | string[],
): Promise<PaidItem[]> => {
  const requestData: CheckTicketStatusRequest = {
    companyIdentification: environment.api.companyIdentification,
    channel: environment.api.channel,
    document,
    placa: Array.isArray(selectedVehicles)
      ? selectedVehicles.join(",")
      : selectedVehicles,
  };

  const response = await automaticApi.post<CheckTicketStatusResponse>(
    "/internalwebapi/v1/vehicle-debts/check-ticket-status",
    requestData,
  );

  const isSuccess = response.data?.status?.codigoRetorno === 0;
  const data = response.data?.data;

  if (isSuccess && data) {
    return data;
  } else {
    throw new Error(response.data?.status?.mensagem || "Erro ao buscar dados");
  }
};

export const usePaidItems = () => {
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  let vehiclesToQuery: string[] | null = null;

  if (customer?.selectedVehicles?.length) {
    vehiclesToQuery = customer.selectedVehicles;
  } else if (customer?.vehicles?.length && customer.vehicles[0]?.plate) {
    vehiclesToQuery = [customer.vehicles[0].plate];
  }

  const { data, isLoading, error, refetch, isError, isSuccess } = useQuery({
    queryKey: ["paidItems", customer?.client?.document, vehiclesToQuery],
    queryFn: () => {
      if (!customer?.client?.document || !vehiclesToQuery) {
        return Promise.resolve([]);
      }
      return fetchPaidItems(customer.client.document, vehiclesToQuery);
    },
    enabled: !!(customer?.client?.document && vehiclesToQuery),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Usar a função de transformação para PaidItems
  const formattedItems = getFormattedPaidItems(data || []);

  return {
    isLoading,
    items: formattedItems,
    error: error?.message || null,
    refetch,
    isError,
    isSuccess,
    data,
  };
};
